import { CenteredSpinner } from '~/commonComponents';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearSuccess } from '~/actions/success';
import { OutlineWarning01Icon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { Typography } from '@tempmee/react';
import { BackButton, Box, Center, ConfirmPopup, ZStack } from '~/commonComponents';
import { useIsSuccess, useNavigationDetector } from '~/hooks';
import { useTrackEvent } from '~/hooks/useTrackEvent';
import { POSTHOG_EVENTS } from '~/services/posthog';
import { isObjectEqual } from '~/utils/Object';
import { fetchNotificationSettings, updateNotificationSettings } from '../../actions';
import { createLoadingSelector } from '../../api/selectors';
import Button from '../../commonComponents/Button';
import { WarningBanner } from '../../commonComponents';
import SaveNotificationSettingsPopup from '../SaveNotificationSettingsPopup';
import { Content } from './components/Content';
import { useNotificationTypeMap } from './hooks/useNotificationSettings';
import { getAllItems } from './utils';

const loadingFecthSelector = createLoadingSelector(['FETCH_NOTIFICATION_SETTINGS']);
const loadingSaveSelector = createLoadingSelector(['UPDATE_NOTIFICATION_SETTINGS']);

export default function NotificationSettings() {
  const dispatch = useDispatch();
  const trackEvent = useTrackEvent();
  const notificationSettings = useSelector((state) => state.user.notificationSettings);
  const isFetching = useSelector((state) => loadingFecthSelector(state));
  const isSaving = useSelector((state) => loadingSaveSelector(state));
  const [initialAllSettings, setInitialAllSettings] = useState([]);
  const [currentSettings, setCurrentSettings] = useState({});
  const { shouldShowWarning, groupAndSortNotifications, notificationTypeMap } = useNotificationTypeMap();
  const allSettings = useMemo(() => getAllItems(currentSettings), [currentSettings]);
  const showNotificationWarning = allSettings?.some(({ event, ...settings }) =>
    shouldShowWarning(event, settings),
  );
  const [showSaveSettingsPopup, setShowSaveSettingsPopup] = useState(false);
  const hasChanges = useMemo(
    () => !isObjectEqual(initialAllSettings, allSettings),
    [initialAllSettings, allSettings],
  );
  const { isSuccess: isNotificationSettingsUpdated } = useIsSuccess([
    'UPDATE_NOTIFICATION_SETTINGS',
  ]);

  const [confirmationExitModalOpen, setConfirmationExitModalOpen] = useState(false);

  const { allowNavigation, blockNavigation, clearPendingNavigation, navigateToPending } =
    useNavigationDetector({
      when: hasChanges && !isNotificationSettingsUpdated,
      onNavigationAttempt: () => {
        setConfirmationExitModalOpen(true);
        blockNavigation();
      },
    });

  const handleOnSwitchChange = (group, code, checked, eventType) => {
    // Track notification toggle event
    const notificationTitle = notificationTypeMap[eventType]?.title || eventType;
    trackEvent(POSTHOG_EVENTS.OFFICE_NOTIFICATION_TOGGLED, {
      notificationType: notificationTitle,
      section: group,
      channel: code, // 'email', 'push', or 'text'
      enabled: checked,
      eventType: eventType,
    });

    const newSettingsArr = currentSettings[group]?.items.map((notification) => {
      if (notification.event === eventType) {
        return { ...notification, [code]: checked };
      }

      return notification;
    });

    setCurrentSettings((prevState) => ({
      ...prevState,
      [group]: { ...prevState[group], items: newSettingsArr },
    }));
  };

  const handleOnSaveButton = () => {
    allowNavigation();
    dispatch(
      updateNotificationSettings({
        notificationSettings: allSettings.map(({ email, event, push, text }) => ({
          email,
          event,
          push,
          text,
        })),
        callback: () => {
          setShowSaveSettingsPopup('save');
        },
      }),
    );
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(fetchNotificationSettings());

    return () => {
      dispatch(clearSuccess(['UPDATE_NOTIFICATION_SETTINGS']));
    };
  }, [dispatch]);

  useEffect(() => {
    if (notificationSettings) {
      const sortedSettings = groupAndSortNotifications(notificationSettings);
      setInitialAllSettings(getAllItems(sortedSettings));
      setCurrentSettings(sortedSettings);
    }
  }, [notificationSettings]);

  const renderContent = () => {
    if (isFetching) {
      return <CenteredSpinner />;
    }
    if (!notificationSettings) {
      return (
        <p
          style={{
            fontSize: 16,
            color: '#546e7a',
            textAlign: 'center',
            marginTop: 20,
          }}
        >
          There are no settings to show.
        </p>
      );
    }
    if (notificationSettings) {
      return (
        <div
          style={{
            width: '-webkit-fill-available',
            backgroundColor: colors.neutral[50],
            marginRight: 54,
            marginLeft: 54,
            paddingRight: 32,
            paddingLeft: 32,
            paddingTop: 24,
          }}
        >
          {showNotificationWarning && (
            <div style={{ marginBottom: -8 }}>
              <WarningBanner description="You have opted out of an important notification" />
            </div>
          )}
          {notificationSettings && (
            <>
              <Content
                notifications={currentSettings}
                handleOnSwitchChange={handleOnSwitchChange}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginTop: 46,
                  marginBottom: 56,
                }}
              >
                <Button
                  text="Save"
                  disabled={!hasChanges}
                  size="medium"
                  onClick={handleOnSaveButton}
                  isLoading={isSaving}
                />
              </div>
            </>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <div className="component_container">
        <div
          className="floating_container"
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            paddingBottom: 50,
          }}
        >
          <Box position="relative" marginBottom={32} marginTop={60}>
            <Center>
              <ZStack left={20}>
                <BackButton />
              </ZStack>

              <Typography as="span" size="heading_2" bold textAlign="center">
                Notification Settings
              </Typography>
            </Center>
          </Box>

          {renderContent()}
        </div>
      </div>

      {showSaveSettingsPopup && (
        <SaveNotificationSettingsPopup
          showSaveSettingsPopup={showSaveSettingsPopup}
          setShowSaveSettingsPopup={setShowSaveSettingsPopup}
        />
      )}

      {confirmationExitModalOpen && (
        <ConfirmPopup
          Icon={() => <OutlineWarning01Icon size={80} color={colors.primary[500]} />}
          leftButtonAction={() => {
            clearPendingNavigation();
            setConfirmationExitModalOpen(false);
          }}
          leftButtonText="Go Back"
          rightButtonAction={navigateToPending}
          rightButtonText="Leave Anyway"
          title="Are You Sure You Want to Leave?"
          titleStyle={{ marginBottom: 20, marginTop: 20 }}
          descriptionStyle={{ marginBottom: 40 }}
          description="Updates made to your notification settings haven't been saved. Do you still want to leave?"
        />
      )}
    </>
  );
}
